import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';

import { hasuraClient } from '../hasura';

import { getChecklistResults } from '../projectSetupChecklist';
import { DefaultRow } from './rows';
import { readableTimeSince } from '../util';

import {
  Card as MuiCard,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  withStyles
} from '@material-ui/core';

import { green, red, yellow } from '@material-ui/core/colors';

import {
  AutocompleteInput,
  BooleanField,
  Button,
  ChipField,
  Datagrid,
  DateField,
  Edit,
  FilterList as FilterListAside,
  FilterListItem,
  List,
  ReferenceArrayField,
  ReferenceInput,
  SelectInput,
  Show,
  SimpleForm,
  SingleFieldList,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useNotify,
  useRedirect,
  useRefresh
} from 'react-admin';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import { DateInput } from "react-admin-date-inputs2";
import DateFnsUtils from '@date-io/date-fns';

import { addMonths, endOfMonth, startOfMonth, subMonths } from 'date-fns';

import format from 'date-fns/format'

import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ProjectSchemaEditPage from './projectSchemaEditPage';
import { useMutation as apolloUseMutation } from "@apollo/client/react/hooks/useMutation";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from '@material-ui/core/DialogActions';
import { Form } from "react-final-form";
import { nameLikeSearch } from "./drawings";
import { analyticsTrackEvent } from "../utils/segment";

// noinspection GraphQLUnresolvedReference
const PROJECT_CHECKLIST = gql`
    query Project($id: Int!) {
        Project(id: $id) {
            id
            name
            companies {
                id
                name
                type
                company_trade
                location
                reportTemplates {
                    id
                    name
                }
            }
        }
        allDrawings(filter: {project_id: $id}) {
            id
            display_name
            created_at
            overlays {
                id
            }
        }
        allRecordTypes(filter: {project_id: $id}) {
            id
            name
            category
            sub_category
            descriptions {
                id
                name
                type
                order
                listValues {
                    id
                    value
                    order
                }
            }
        }
        allColourStatus(filter: {project_id: $id}) {
            id
            status
            colour
        }
    }
`;

// noinspection GraphQLUnresolvedReference
const PROJECT_ACTIVITY = gql`
    query projectLastUpdated($id: bigint!) {
        mostRecentUsersRecordUpdate: website_prod_record_updated(where: {project_name: {}, _and: {_not: {User: {company_id: {_eq: "1"}}}}, project_id: {_eq: $id}}, order_by: {user_id: asc, timestamp: desc}, distinct_on: user_id) {
            timestamp
            User {
                id
                company_name
                first_name
                last_name
                email
                job_title
                company_trade
                company_type
            }
            platform
            os
        }
        mostRecentUsersRecordsUpdate: website_prod_records_updated(where: {project_name: {}, _and: {_not: {User: {company_id: {_eq: "1"}}}}, project_id: {_eq: $id}}, order_by: {user_id: asc, timestamp: desc}, distinct_on: user_id) {
            timestamp
            User {
                id
                company_name
                first_name
                last_name
                email
                job_title
                company_trade
                company_type
            }
            platform
            os
        }
        oldestRecordUpdated: website_prod_record_updated(limit: 1, order_by: {timestamp: asc}) {
            timestamp
        }
        oldestRecordsUpdated: website_prod_records_updated(limit: 1, order_by: {timestamp: asc}) {
            timestamp
        }
    }
`;

const Card = withStyles(theme => ({
  root: {
    [theme.breakpoints.up('sm')]: {
      order: -1, // display on the left rather than on the right of the list
      width: '15em',
      marginRight: '1em',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))(MuiCard);

const ProjectStartDateFilter = () => {
  return (
    <FilterListAside label="Project Start" icon={<AccessTimeIcon/>}>
      <FilterListItem
        label="Next Month"
        value={{
          start_date__gte: startOfMonth(addMonths(new Date(), 1)).toISOString(),
          start_date__lte: startOfMonth(addMonths(new Date(), 2)).toISOString(),
        }}
      />
      <FilterListItem
        label="This Month"
        value={{
          start_date__gte: startOfMonth(new Date()).toISOString(),
          start_date__lte: endOfMonth(new Date()).toISOString(),
        }}
      />
      <FilterListItem
        label="Last month"
        value={{
          start_date__gte: subMonths(startOfMonth(new Date()), 1).toISOString(),
          start_date__lte: startOfMonth(new Date()).toISOString(),
        }}
      />
      <FilterListItem
        label="Earlier"
        value={{
          start_date__gte: undefined,
          start_date__lte: subMonths(startOfMonth(new Date()), 1).toISOString(),
        }}
      />
    </FilterListAside>
  );
};

const ProjectEndDateFilter = () => {
  return (
    <FilterListAside label="Project End" icon={<AccessTimeIcon/>}>
      <FilterListItem
        label="Next Month"
        value={{
          end_date__gte: startOfMonth(addMonths(new Date(), 1)).toISOString(),
          end_date__lte: startOfMonth(addMonths(new Date(), 2)).toISOString(),
        }}
      />
      <FilterListItem
        label="This Month"
        value={{
          end_date__gte: startOfMonth(new Date()).toISOString(),
          end_date__lte: endOfMonth(new Date()).toISOString(),
        }}
      />
      <FilterListItem
        label="Last month"
        value={{
          end_date__gte: subMonths(startOfMonth(new Date()), 1).toISOString(),
          end_date__lte: startOfMonth(new Date()).toISOString(),
        }}
      />
      <FilterListItem
        label="Earlier"
        value={{
          end_date__gte: undefined,
          end_date__lte: subMonths(startOfMonth(new Date()), 1).toISOString(),
        }}
      />
    </FilterListAside>
  );
};

const CreatedAtDateFilter = () => {
  return (
    <FilterListAside label="Created At" icon={<AccessTimeIcon/>}>
      <FilterListItem
        label="This Month"
        value={{
          created_at__gte: startOfMonth(new Date()).toISOString(),
          created_at__lte: undefined,
        }}
      />
      <FilterListItem
        label="Last month"
        value={{
          created_at__gte: subMonths(startOfMonth(new Date()), 1).toISOString(),
          created_at__lte: startOfMonth(new Date()).toISOString(),
        }}
      />
      <FilterListItem
        label="Earlier"
        value={{
          created_at__gte: undefined,
          created_at__lte: subMonths(startOfMonth(new Date()), 1).toISOString(),
        }}
      />
    </FilterListAside>
  );
};

const FilterSidebar = () => {
  return (
    <Card>
      <CardContent>
        <ProjectStartDateFilter/>
        <ProjectEndDateFilter/>
        <CreatedAtDateFilter/>
      </CardContent>
    </Card>
  );
};

const dateParser = (date) => format(new Date(date), 'yyyy-MM');

const projectFilters = [
  <TextInput source="name__like" label="Name" alwaysOn/>,
  <TextInput source="address__like" label="Address"/>,
  <SelectInput
    choices={[
      { id: 'vip', name: 'vip' },
      { id: 'self-service', name: 'self-service' },
    ]}
    source="classification"
    label="Classification"
    alwaysOn
  />,
  <SelectInput
    choices={[
      { id: false, name: 'No' },
      { id: true, name: 'Yes' },
    ]}
    source="is_archived"
    label="Archived?"
    defaultValue={false}
    alwaysOn
  />,
  <TextInput source="comments__like" label="Comments"/>,
  // Filter by Year and Month
  <MuiPickersUtilsProvider utils={DateFnsUtils} label="Created At" source="created_at__like">
    <DateInput
      source="created_at__like"
      type="date"
      label="Created At"
      openTo="year"
      parse={dateParser}
      options={{
        dateFormat: "yyyy-MM",
        views: ["year", "month"],
        disableFuture: true,
      }}
    />
  </MuiPickersUtilsProvider>,
  <TextInput source="owner__like" label="Owner"/>
];

export const ProjectList = props => (
  <List aside={<FilterSidebar/>} filters={projectFilters} {...props} bulkActionButtons={[]}>
    <Datagrid rowClick="show">
      <TextField source="id"/>
      <TextField source="name"/>
      <TextField source="classification"/>
      <DateField source="start_date"/>
      <DateField source="end_date"/>
      <DateField source="created_at"/>
      <BooleanField label="Archived?" source="is_archived"/>
      <DateField label="Archived Date" source="archived_at"/>
      <ReferenceArrayField label="Company" reference="Company" source="companiesIds">
        <SingleFieldList link="show">
          <ChipField source="name"/>
        </SingleFieldList>
      </ReferenceArrayField>
      {/*<ReferenceArrayField source="companiesIds" reference="companies"><TextField source="id" /></ReferenceArrayField>*/}
    </Datagrid>
  </List>
);

const ProjectTitle = ({ record }) => {
  return <span>Project{record ? `: ${record.name}` : ''}</span>;
};

let getProjectChecklist = (loading, data, error) => {
  if (loading) {
    return <div>Loading...</div>;
  }

  let newData = {...data};

  if (data && data.allRecordTypes) {
    const updatedRecordTypes = data.allRecordTypes.map(rt => ({
      ...rt,
      descriptions: [...rt.descriptions]
          .sort((a, b) => a.order - b.order) // Sort descriptions immutably
          .map(d => ({
              ...d,
              listValues: [...d.listValues]
                  .sort((a, b) => a.order - b.order) // Sort listValues immutably
          }))
    }));
    newData = {...data, allRecordTypes: updatedRecordTypes};
  }
  const rows = getChecklistResults(newData);

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell/>
            <TableCell>Criteria</TableCell>
            <TableCell align="center">Requirement</TableCell>
            <TableCell align="center">Value</TableCell>
            <TableCell align="center">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
              if (row.override) {
                return row.override(row);
              } else {
                return (<DefaultRow row={row}/>)
              }
            }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

let getProjectActivity = (loading, data, error, lastWeek, lastMonth) => {
  if (loading || !data) {
    return <div>Loading...</div>;
  }
  let getRowColor = (timestamp) => {
    if (timestamp > lastWeek.toISOString()) return green[100];
    if (timestamp > lastMonth.toISOString()) return yellow[100];
    return red[100];
  }

  if (!data.mostRecentUserCombinedUpdates.length) {
    const oldestUpdate = data.oldestRecordUpdated[0].timestamp < data.oldestRecordsUpdated[0].timestamp ? data.oldestRecordUpdated[0].timestamp : data.oldestRecordsUpdated[0].timestamp;
    return (
      <CardContent>
        <Typography variant="body2" component="p">
          No activity to display for this project.
          <br/> <br/>
          There are no stored updates on this project (excluding Matrak users) since {oldestUpdate}
        </Typography>
      </CardContent>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Job Title</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Trade</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Device</TableCell>
            <TableCell>Last Record Updated</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.mostRecentUserCombinedUpdates.map((u) => {
              return (
                <TableRow style={{ backgroundColor: getRowColor(u.timestamp) }}>
                  <TableCell>{u.User.first_name} {u.User.last_name} ({u.User.email})</TableCell>
                  <TableCell>{u.User.job_title}</TableCell>
                  <TableCell>{u.User.company_name}</TableCell>
                  <TableCell>{u.User.company_trade}</TableCell>
                  <TableCell>{u.User.company_type}</TableCell>
                  <TableCell>{getPlatformString(u.platform, u.os)}</TableCell>
                  <TableCell>{readableTimeSince(new Date(u.timestamp))}</TableCell>
                </TableRow>
              )
            }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

let getPlatformString = (platform, os) => {
  if (platform === 'expo') platform = 'V2';
  platform = platform.charAt(0).toUpperCase() + platform.slice(1);
  if (!os) return platform;
  return `${platform} (${os})`;
};

export const getRecentDate = (daysLess) => {
  const d = new Date();
  d.setDate(d.getDate() - daysLess);
  return d;
};

// merge website_prod_record_updated and website_prod_records_updated into mostRecentUserCombinedUpdates
let cleanActivityData = (data, setData) => {
  if (!data) return;

  let updatesMap = {};
  for (let u of data.mostRecentUsersRecordUpdate) {
    updatesMap[u.User.id] = u;
  }
  for (let u of data.mostRecentUsersRecordsUpdate) {
    if (!updatesMap[u.User.id] || updatesMap[u.User.id].timestamp > u.timestamp)
      updatesMap[u.User.id] = u;
  }
  let mostRecentUserCombinedUpdates = Object.values(updatesMap).sort((a, b) => (a.timestamp < b.timestamp) ? 1 : ((b.timestamp < a.timestamp) ? -1 : 0));
  setData({ ...data, mostRecentUserCombinedUpdates });
};

export function ProjectShow(props) {
  const [timestampLastWeek] = useState(getRecentDate(7));
  const [timestampLastMonth] = useState(getRecentDate(30));
  const [activity, setActivity] = useState(null);

  const { loading: checklistLoading, error: checklistError, data: checklistData } = useQuery(PROJECT_CHECKLIST, {
    variables: { id: parseInt(props.id) },
  });

  const { loading: activityLoading, error: activityError, data: activityData } = useQuery(PROJECT_ACTIVITY, {
    variables: { id: parseInt(props.id) },
    client: hasuraClient,
  });

  if (!activityLoading && !activity) {
    cleanActivityData(activityData, setActivity);
  }

  return (
    <Show title={<ProjectTitle/>} {...props} actions={<ProjectShowActions/>}>
      <TabbedShowLayout syncWithLocation={false}>
        <Tab label="Summary">
          <ReferenceArrayField label="Companies On Project" reference="Company" source="companiesIds">
            <SingleFieldList link="show">
              <ChipField source="name"/>
            </SingleFieldList>
          </ReferenceArrayField>
          <TextField source="id"/>
          <TextField source="name"/>
          <TextField source="classification"/>
          <TextField source="address"/>
          <TextField source="owner"/>
          <TextField source="contact_details"/>
          <TextField source="comments"/>
          <BooleanField label="Archived?" source="is_archived"/>
          <DateField source="archived_at"/>
        </Tab>
        <Tab label="Schema">
          <ProjectSchemaEditPage projectId={props.id}/>
        </Tab>
        <Tab label="Project Setup Checklist">
          {getProjectChecklist(checklistLoading, checklistData, checklistError)}
        </Tab>
        <Tab label="Project Activity">
          {getProjectActivity(activityLoading, activity, activityError, timestampLastWeek, timestampLastMonth)}
        </Tab>
        <Tab label="Companies">
          <ReferenceArrayField label="Companies" reference="Company" source="companiesIds">
            <Datagrid rowClick="show">
              <TextField source="id"/>
              <TextField source="name" label="Name"/>
              <TextField source="type" label="Type"/>
              <TextField source="company_trade" label="Trade"/>
              <TextField source="location" label="Location"/>
              {/*<ArrayField source="projects"><SingleFieldList><ChipField source="id" /></SingleFieldList></ArrayField>*/}
              {/*<ReferenceArrayField source="projectsIds" reference="projects"><TextField source="id" /></ReferenceArrayField>*/}
              {/*<ArrayField source="reportTemplates"><SingleFieldList><ChipField source="id" /></SingleFieldList></ArrayField>*/}
              {/*<ReferenceArrayField source="reportTemplatesIds" reference="reportTemplates"><TextField source="id" /></ReferenceArrayField>*/}
              {/*<TextField source="updated_at" />*/}
              {/*<NumberField source="auto_approve" />*/}
            </Datagrid>
          </ReferenceArrayField>
        </Tab>
        <Tab label="Groups">
          <ReferenceArrayField label="Groups" reference="Group" source="groupsIds">
            <Datagrid rowClick="show">
              <TextField source="id"/>
              <TextField source="name" label="Name"/>
              <TextField source="is_default" label="Default"/>
              <ReferenceArrayField label="Company" reference="Company" source="companiesIds">
                <SingleFieldList link="show">
                  <ChipField source="name"/>
                </SingleFieldList>
              </ReferenceArrayField>
            </Datagrid>
          </ReferenceArrayField>
        </Tab>
        {/*<Tab label="Drawings">*/}
        {/*  <ReferenceArrayField label="Drawings" reference="Drawing" source="drawingsIds">*/}
        {/*    <Datagrid rowClick="show">*/}
        {/*      <TextField source="id" />*/}
        {/*      <TextField source="display_name" label="Name" />*/}
        {/*    </Datagrid>*/}
        {/*  </ReferenceArrayField>*/}
        {/*</Tab>*/}
      </TabbedShowLayout>
    </Show>
  )
}


export const ProjectEditView = (props) => {
  return (
    <Edit title='Edit Project' {...props} undoable={false}>
      <SimpleForm>
        <TextInput disabled source="id"/>
        <TextInput source="name"/>
        <TextInput source="address"/>
        <TextInput source="owner"/>
        <TextInput source="contact_details"/>
        <MuiPickersUtilsProvider utils={DateFnsUtils} label="Start Date" source="start_date">
          <DateInput source="start_date" label={'Start Date'} options={{
            format: "yyyy-MM-dd",
            views: ["year", "month", "date"],
          }}/>
        </MuiPickersUtilsProvider>
        <MuiPickersUtilsProvider utils={DateFnsUtils} label="End Date" source="end_date">
          <DateInput source="end_date" label={'End Date'} options={{
            format: "yyyy-MM-dd",
            views: ["year", "month", "date"],
          }}/>
        </MuiPickersUtilsProvider>
        <SelectInput source="classification"
                     choices={[{ id: '', name: 'None' }, { id: 'matrak-dev', name: 'Matrak Dev' }, {
                       id: 'matrak-demo',
                       name: 'Matrak Demo'
                     }, { id: 'customer-demo', name: 'Customer Demo' }, {
                       id: 'self-service',
                       name: 'Self Service'
                     }, { id: 'vip', name: 'VIP' }]}/>
      </SimpleForm>
    </Edit>
  )
}


const ProjectShowActions = ({ basePath, data, record }) => {
  const redirectTo = useRedirect();
  return (
    <TopToolbar>
      <AddCompanyToProject basePath={basePath} record={data}/>
      <ArchiveUnarchiveProject basePath={basePath} record={data}/>
      <Button label="Edit" onClick={() => {
        redirectTo(`/Project/${data.id}/edit`);
      }}/>
    </TopToolbar>
  )
};


const AddCompanyToProject = ({ record }) => {
  const [open, setOpen] = useState(false);
  const redirectTo = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();


  const [removeCompanyFromProject] = apolloUseMutation(gql`
      mutation removeCompanyFromProject($companyId: Int!, $projectId: Int!) {
          removeCompanyFromProject(
              companyId: $companyId
              projectId: $projectId
          ) {
              id
              name
          }
      }
  `);

  const [addCompanyToProject] = apolloUseMutation(gql`
      mutation addCompanyToProject($companyId: Int!, $projectId: Int!) {
          addCompanyToProject(
              companyId: $companyId
              projectId: $projectId
          ) {
              id
              name
          }
      }
  `);

  const handleAdd = async (args) => {
    try {
      await addCompanyToProject({
        variables: {
          companyId: args.companyId,
          projectId: record.id,
        },
      });
      analyticsTrackEvent("Company Added To Project", "Company", {
        projectId: record.id,
        addedCompanyId: args.companyId,
      });
      notify("Saved");
      redirectTo(`/Project/${record.id}/show`);
      refresh();
      setOpen(false);
    } catch (e) {
      notify(e.toString());
    }
  };

  const handleRemove = async (args) => {
    try {
      await removeCompanyFromProject({
        variables: {
          companyId: args.companyId,
          projectId: record.id,
        },
      });
      analyticsTrackEvent("Company Removed From Project", "Company", {
        projectId: record.id,
        removedCompanyId: args.companyId,
      });
      notify("Saved");
      redirectTo(`/Project/${record.id}/show`);
      refresh();
      setOpen(false);
    } catch (e) {
      notify(e.toString());
    }
  };

  const handleForm = (async (a, b) => {
    if (a.add_or_remove === 'add') {
      await handleAdd(a);
    } else if (a.add_or_remove === 'remove') {
      await handleRemove(a);
    }
  })

  return (
    <>
      <Button label={'Add/Remove Company From Project'} variant={'outlined'} color="primary"
              onClick={() => setOpen(true)}/>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add company to project</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleForm} render={({ handleSubmit }) => (
            <div>
              <ReferenceInput label="Company" source="companyId" reference="Company" filterToQuery={nameLikeSearch} perPage={1000}
                              alwaysOn>
                <AutocompleteInput source="name"/>
              </ReferenceInput>
              <SelectInput
                choices={[
                  { id: 'add', name: 'Add' },
                  { id: 'remove', name: 'Remove' },
                ]} source={'add_or_remove'}/>
              <Button label="Save" onClick={() => handleSubmit('add')}/>
            </div>
          )}>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ArchiveUnarchiveProject = ({ record }) => {
  const [open, setOpen] = useState(false);
  const redirectTo = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();


  const [archiveProject] = apolloUseMutation(gql`
      mutation archiveProject($projectId: Int!) {
          archiveProject(
              projectId: $projectId
          ) {
              id
              name
          }
      }
  `);

  const [unarchiveProject] = apolloUseMutation(gql`
      mutation unarchiveProject($projectId: Int!) {
          unarchiveProject(
              projectId: $projectId
          ) {
              id
              name
          }
      }
  `);

  const handleConfirm = async (args) => {
    try {
      if (record?.is_archived) {
        await unarchiveProject({
          variables: {
            projectId: record.id,
          },
        });
        analyticsTrackEvent("Project Unarchived", "Project", {
          projectId: record.id,
          projectName: record.name,
          projectClassification: record.classification,
          projectOwner: record.owner,
        });
        notify("Project Unarchived");
      } else {
        await archiveProject({
          variables: {
            projectId: record.id,
          },
        });
        analyticsTrackEvent("Project Archived", "Project", {
          projectId: record.id,
          projectName: record.name,
          projectClassification: record.classification,
          projectOwner: record.owner,
        });
        notify("Project Archived");
      }
      redirectTo(`/Project/${record.id}/show`);
      refresh();
      setOpen(false);
    } catch (e) {
      notify(e.toString());
    }
  };

  return (
    <>
      <Button label={record?.is_archived ? 'Unarchive Project' : 'Archive Project'} variant={'outlined'} color="danger"
              onClick={() => setOpen(true)}/>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Are you sure you want to {record?.is_archived ? 'unarchive' : 'archive'} this project?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            The following companies will {record?.is_archived ? 'gain' : 'lose'} access to update this project:
            <br/><br/>
            <ReferenceArrayField label="Company" reference="Company" source="companiesIds">
              <SingleFieldList link="show">
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>
          </DialogContentText>
          <DialogActions>
            <Button label="Cancel" onClick={() => setOpen(false)} />
            <Button label="Confirm" onClick={handleConfirm} />
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
}
